import { useState, useEffect } from 'react'
import { ReleaseFilters as ReleaseFiltersType } from '../../types/release'
import { useGenres, useStyles, useCountries, useReleaseFacets } from '../../hooks'
import { Button } from '../common'
import styles from './Releases.module.css'

interface ReleaseFiltersProps {
  filters: ReleaseFiltersType
  onFiltersChange: (filters: ReleaseFiltersType) => void
  onClose?: () => void
  contextReleaseIds?: number[]
}

export function ReleaseFilters({ filters, onFiltersChange, onClose, contextReleaseIds }: ReleaseFiltersProps) {
  // Derive context filters (everything except genre/style/country/pagination/sort/modes)
  const { genres: _g, styles: _s, countries: _c, page: _p, per_page: _pp, sort: _so, order: _o, shuffle: _sh, genre_mode: _gm, style_mode: _sm, ...contextFilters } = filters

  // If contextReleaseIds are provided, use them to scope facets (collection/wantlist/seller pages)
  const facetContext = contextReleaseIds && contextReleaseIds.length > 0
    ? { ...contextFilters, release_ids: contextReleaseIds }
    : contextFilters

  const hasContext = !!(
    contextReleaseIds?.length ||
    facetContext.q || facetContext.artist_id || facetContext.label ||
    (facetContext.release_ids && facetContext.release_ids.length > 0) ||
    facetContext.year_from || facetContext.year_to ||
    facetContext.min_want || facetContext.min_median || facetContext.max_median
  )

  const { data: facetsData, isLoading: facetsLoading } = useReleaseFacets(facetContext, { enabled: hasContext })
  const { data: globalGenresData, isLoading: genresQueryLoading } = useGenres()
  const { data: globalStylesData, isLoading: stylesQueryLoading } = useStyles()
  const { data: countriesData, isLoading: countriesLoading } = useCountries()

  const genreItems = hasContext
    ? (facetsData?.genres ?? globalGenresData?.genres ?? [])
    : (globalGenresData?.genres ?? [])
  const styleItems = hasContext
    ? (facetsData?.styles ?? globalStylesData?.styles ?? [])
    : (globalStylesData?.styles ?? [])

  const genresLoading = hasContext ? facetsLoading : genresQueryLoading
  const stylesLoading = hasContext ? facetsLoading : stylesQueryLoading

  const [genreSearch, setGenreSearch] = useState('')
  const [styleSearch, setStyleSearch] = useState('')
  const [countrySearch, setCountrySearch] = useState('')
  const [genreMode, setGenreMode] = useState<'or' | 'and'>(filters.genre_mode || 'or')
  const [styleMode, setStyleMode] = useState<'or' | 'and'>(filters.style_mode || 'or')
  const [minWant, setMinWant] = useState(filters.min_want?.toString() || '')
  const [minMedian, setMinMedian] = useState(filters.min_median?.toString() || '')
  const [maxMedian, setMaxMedian] = useState(filters.max_median?.toString() || '')
  const [yearFrom, setYearFrom] = useState(filters.year_from?.toString() || '')
  const [yearTo, setYearTo] = useState(filters.year_to?.toString() || '')
  const [selectedGenres, setSelectedGenres] = useState<string[]>(filters.genres || [])
  const [selectedStyles, setSelectedStyles] = useState<string[]>(filters.styles || [])
  const [selectedCountries, setSelectedCountries] = useState<string[]>(filters.countries || [])

  useEffect(() => {
    setMinWant(filters.min_want?.toString() || '')
    setMinMedian(filters.min_median?.toString() || '')
    setMaxMedian(filters.max_median?.toString() || '')
    setYearFrom(filters.year_from?.toString() || '')
    setYearTo(filters.year_to?.toString() || '')
    setSelectedGenres(filters.genres || [])
    setSelectedStyles(filters.styles || [])
    setSelectedCountries(filters.countries || [])
    setGenreMode(filters.genre_mode || 'or')
    setStyleMode(filters.style_mode || 'or')
  }, [filters])

  const handleApplyFilters = () => {
    onFiltersChange({
      ...filters,
      min_elevated_score: undefined,
      max_last_sold_days: undefined,
      min_want: minWant ? parseInt(minWant) : undefined,
      min_median: minMedian ? parseFloat(minMedian) : undefined,
      max_median: maxMedian ? parseFloat(maxMedian) : undefined,
      year_from: yearFrom ? parseInt(yearFrom) : undefined,
      year_to: yearTo ? parseInt(yearTo) : undefined,
      genres: selectedGenres.length > 0 ? selectedGenres : undefined,
      styles: selectedStyles.length > 0 ? selectedStyles : undefined,
      countries: selectedCountries.length > 0 ? selectedCountries : undefined,
      genre_mode: genreMode,
      style_mode: styleMode,
      page: 1,
    })
    onClose?.()
  }

  const handleReset = () => {
    setMinWant('')
    setMinMedian('')
    setMaxMedian('')
    setYearFrom('')
    setYearTo('')
    setSelectedGenres([])
    setSelectedStyles([])
    setSelectedCountries([])
    setGenreSearch('')
    setStyleSearch('')
    setCountrySearch('')
    onFiltersChange({ page: 1, per_page: filters.per_page, sort: filters.sort, order: filters.order, q: filters.q })
    onClose?.()
  }

  const toggleGenre = (genre: string) => {
    setSelectedGenres(prev =>
      prev.includes(genre) ? prev.filter(g => g !== genre) : [...prev, genre]
    )
  }

  const toggleStyle = (style: string) => {
    setSelectedStyles(prev =>
      prev.includes(style) ? prev.filter(s => s !== style) : [...prev, style]
    )
  }

  const toggleCountry = (country: string) => {
    setSelectedCountries(prev =>
      prev.includes(country) ? prev.filter(c => c !== country) : [...prev, country]
    )
  }

  const filteredGenres = genreItems.filter(g =>
    g.name.toLowerCase().includes(genreSearch.toLowerCase())
  )

  const filteredStyles = styleItems.filter(s =>
    s.name.toLowerCase().includes(styleSearch.toLowerCase())
  )

  const countryItems = countriesData?.countries ?? []
  const filteredCountries = countryItems.filter(c =>
    c.name.toLowerCase().includes(countrySearch.toLowerCase())
  )

  return (
    <div className={styles.filterPanel} data-tour="filter-panel">
      <div className={styles.filterMobileHeader}>
        <div>
          <span className={styles.filterMobileEyebrow}>Refine results</span>
          <h2>Filters</h2>
        </div>
        {onClose && (
          <button className={styles.filterMobileClose} onClick={onClose} aria-label="Close filters">
            <i className="bi bi-x-lg"></i>
          </button>
        )}
      </div>
      {/* Genre / Style columns */}
      <div className={styles.filterColumns}>
        <div className={styles.filterColumn}>
          <div className={styles.filterColumnHeader}>
            <span className={styles.filterColumnLabel}>
              <i className="bi bi-music-note-beamed"></i> Genres
            </span>
            <div className={styles.filterModeToggle}>
              <button
                className={`${styles.filterModeBtn} ${genreMode === 'or' ? styles.filterModeBtnActive : ''}`}
                onClick={() => setGenreMode('or')}
                title="Match any selected genre (OR)"
              >
                <i className="bi bi-union"></i>
              </button>
              <button
                className={`${styles.filterModeBtn} ${genreMode === 'and' ? styles.filterModeBtnActive : ''}`}
                onClick={() => setGenreMode('and')}
                title="Match all selected genres (AND)"
              >
                <i className="bi bi-intersect"></i>
              </button>
            </div>
            <div className={styles.filterColumnSearch}>
              <i className="bi bi-search"></i>
              <input
                type="text"
                placeholder="Search Genres"
                value={genreSearch}
                onChange={(e) => setGenreSearch(e.target.value)}
              />
            </div>
          </div>
          <div className={styles.filterColumnList}>
            {genresLoading && genreItems.length === 0 ? (
              <FilterListSkeleton />
            ) : filteredGenres.length === 0 ? (
              <div className={styles.filterColumnEmpty}>No genres</div>
            ) : filteredGenres.map(genre => (
              <label key={genre.name} className={styles.checkboxItem}>
                <input
                  type="checkbox"
                  checked={selectedGenres.includes(genre.name)}
                  onChange={() => toggleGenre(genre.name)}
                />
                <span className={styles.checkboxCustom} data-checked={selectedGenres.includes(genre.name) ? '' : undefined} />
                <span className={styles.checkboxLabel}>
                  {genre.name}
                  <span className={styles.checkboxCount}>({genre.count.toLocaleString()})</span>
                </span>
              </label>
            ))}
          </div>
        </div>

        <div className={styles.filterColumn} data-tour="style-filter-col">
          <div className={styles.filterColumnHeader}>
            <span className={styles.filterColumnLabel}>
              <i className="bi bi-gear"></i> Styles
            </span>
            <div className={styles.filterModeToggle} data-tour="style-mode-toggle">
              <button
                className={`${styles.filterModeBtn} ${styleMode === 'or' ? styles.filterModeBtnActive : ''}`}
                onClick={() => setStyleMode('or')}
                title="Match any selected style (OR)"
              >
                <i className="bi bi-union"></i>
              </button>
              <button
                className={`${styles.filterModeBtn} ${styleMode === 'and' ? styles.filterModeBtnActive : ''}`}
                onClick={() => setStyleMode('and')}
                title="Match all selected styles (AND)"
              >
                <i className="bi bi-intersect"></i>
              </button>
            </div>
            <div className={styles.filterColumnSearch}>
              <i className="bi bi-search"></i>
              <input
                type="text"
                placeholder="Search Styles"
                value={styleSearch}
                onChange={(e) => setStyleSearch(e.target.value)}
              />
            </div>
          </div>
          <div className={styles.filterColumnList}>
            {stylesLoading && styleItems.length === 0 ? (
              <FilterListSkeleton />
            ) : filteredStyles.length === 0 ? (
              <div className={styles.filterColumnEmpty}>No styles</div>
            ) : filteredStyles.map(style => (
              <label key={style.name} className={styles.checkboxItem}>
                <input
                  type="checkbox"
                  checked={selectedStyles.includes(style.name)}
                  onChange={() => toggleStyle(style.name)}
                />
                <span className={styles.checkboxCustom} data-checked={selectedStyles.includes(style.name) ? '' : undefined} />
                <span className={styles.checkboxLabel}>
                  {style.name}
                  <span className={styles.checkboxCount}>({style.count.toLocaleString()})</span>
                </span>
              </label>
            ))}
          </div>
        </div>

        <div className={styles.filterColumn}>
          <div className={styles.filterColumnHeader}>
            <span className={styles.filterColumnLabel}>
              <i className="bi bi-globe"></i> Countries
            </span>
            <div className={styles.filterColumnSearch}>
              <i className="bi bi-search"></i>
              <input
                type="text"
                placeholder="Search Countries"
                value={countrySearch}
                onChange={(e) => setCountrySearch(e.target.value)}
              />
            </div>
          </div>
          <div className={styles.filterColumnList}>
            {countriesLoading && countryItems.length === 0 ? (
              <FilterListSkeleton />
            ) : filteredCountries.length === 0 ? (
              <div className={styles.filterColumnEmpty}>No countries</div>
            ) : filteredCountries.map(country => (
              <label key={country.name} className={styles.checkboxItem}>
                <input
                  type="checkbox"
                  checked={selectedCountries.includes(country.name)}
                  onChange={() => toggleCountry(country.name)}
                />
                <span className={styles.checkboxCustom} data-checked={selectedCountries.includes(country.name) ? '' : undefined} />
                <span className={styles.checkboxLabel}>
                  {country.name}
                  <span className={styles.checkboxCount}>({country.count.toLocaleString()})</span>
                </span>
              </label>
            ))}
          </div>
        </div>
      </div>

      {/* Numeric Filters */}
      <div className={styles.filterGrid}>
        <div className={styles.filterGroup}>
          <label>Min Want</label>
          <input type="number" value={minWant} onChange={(e) => setMinWant(e.target.value)} placeholder="e.g. 100" />
        </div>
        <div className={styles.filterGroup}>
          <label>Min Price</label>
          <input type="number" step="0.01" value={minMedian} onChange={(e) => setMinMedian(e.target.value)} placeholder="e.g. 10" />
        </div>
        <div className={styles.filterGroup}>
          <label>Max Price</label>
          <input type="number" step="0.01" value={maxMedian} onChange={(e) => setMaxMedian(e.target.value)} placeholder="e.g. 50" />
        </div>
        <div className={styles.filterGroup}>
          <label>Year From</label>
          <input type="number" value={yearFrom} onChange={(e) => setYearFrom(e.target.value)} placeholder="e.g. 1990" />
        </div>
        <div className={styles.filterGroup}>
          <label>Year To</label>
          <input type="number" value={yearTo} onChange={(e) => setYearTo(e.target.value)} placeholder="e.g. 2000" />
        </div>
      </div>

      <div className={styles.filterActions}>
        <Button variant="primary" onClick={handleApplyFilters} data-tour="apply-filters">
          Apply Filters
        </Button>
        <Button variant="secondary" onClick={handleReset}>
          Reset
        </Button>
      </div>
    </div>
  )
}

function FilterListSkeleton() {
  return (
    <div className={styles.filterColumnSkeleton} aria-hidden="true">
      {Array.from({ length: 8 }).map((_, i) => (
        <span key={i} className={styles.filterSkeletonRow} />
      ))}
    </div>
  )
}
